import React, { useMemo } from "react";
import CustomSelect from "../../globalcontrols/customselect/customselect";
import "./css/hourlyWeekdayHeatmap.css";

const WEEKDAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
const HOURS = Array.from({ length: 24 }, (_, index) => index);

const units = (value) => Number(value || 0).toLocaleString("es-CL", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const money = (value) => new Intl.NumberFormat("es-CL", {
  style: "currency",
  currency: "CLP",
  maximumFractionDigits: 0,
}).format(Number(value || 0));

function ProductHourlyWeekdayHeatmap({
  data,
  error,
  loading,
  locations,
  selectedLocation,
  setSelectedLocation,
  products,
  selectedProduct,
  setSelectedProduct,
}) {
  const cells = useMemo(() => {
    const index = new Map();
    (data?.cells || []).forEach((cell) => index.set(`${cell.weekday}-${cell.hour}`, cell));
    return index;
  }, [data]);
  const maxUnits = Number(data?.max_average_units || 0);
  const dailyAverages = useMemo(() => WEEKDAYS.map((day, dayIndex) => {
    const weekdayCells = HOURS.map((hour) => cells.get(`${dayIndex + 1}-${hour}`));
    return {
      day,
      units: weekdayCells.reduce((total, cell) => total + Number(cell?.average_units || 0), 0),
      revenue: weekdayCells.reduce((total, cell) => total + Number(cell?.average_revenue || 0), 0),
    };
  }), [cells]);

  return (
    <section className="embedded-dashboard-chart hourly-heatmap-card product-hourly-card">
      <div className="card-heading hourly-heatmap-heading">
        <div>
          <p className="card-kicker">Patrón por producto</p>
          <h2>Venta promedio del producto por hora</h2>
          <p>Promedio de unidades e ingresos por hora y día de la semana, usando horario de Chile.</p>
        </div>
        <div className="product-hourly-filters">
          <CustomSelect
            label="Producto"
            options={products}
            selectedValue={selectedProduct}
            onChange={setSelectedProduct}
            labelExtractor={(item) => item.product}
            valueExtractor={(item) => item.code}
            searchable
            searchPlaceholder="Buscar producto…"
          />
          <CustomSelect
            label="Ubicación"
            options={locations}
            selectedValue={selectedLocation}
            onChange={setSelectedLocation}
            labelExtractor={(item) => item.name}
            valueExtractor={(item) => item.id.toString()}
          />
        </div>
      </div>

      {error ? <div className="dashboard-alert" role="alert">{error}</div> : null}
      {loading ? <div className="dashboard-loading" role="status">Calculando promedios del producto…</div> : (
        selectedProduct ? (
          <div className="hourly-heatmap-scroll">
            <div className="hourly-heatmap-layout">
              <div className="hourly-heatmap-grid" role="img" aria-label="Mapa de calor de unidades promedio del producto por día y hora">
                <span className="hourly-heatmap-corner">Hora</span>
                {WEEKDAYS.map((day) => <strong className="hourly-heatmap-day" key={day}>{day}</strong>)}
                {HOURS.flatMap((hour) => {
                  const hourLabel = `${String(hour).padStart(2, "0")}:00`;
                  return [
                    <strong className="hourly-heatmap-hour" key={`hour-${hour}`}>{hourLabel}</strong>,
                    ...WEEKDAYS.map((day, dayIndex) => {
                      const cell = cells.get(`${dayIndex + 1}-${hour}`) || {
                        average_units: 0,
                        average_revenue: 0,
                      };
                      const ratio = maxUnits ? Math.min(1, cell.average_units / maxUnits) : 0;
                      return (
                        <div
                          className="hourly-heatmap-cell"
                          key={`${day}-${hour}`}
                          style={{
                            "--heat": ratio,
                            backgroundColor: `rgba(163, 70, 116, ${0.07 + ratio * 0.88})`,
                            color: ratio > 0.52 ? "#fff" : "#392e34",
                          }}
                          title={`${day} ${hourLabel}–${String(hour + 1).padStart(2, "0")}:00 · ${units(cell.average_units)} unidades promedio · ${money(cell.average_revenue)} promedio`}
                        >
                          {cell.average_units ? units(cell.average_units) : "—"}
                        </div>
                      );
                    }),
                  ];
                })}
                <strong className="hourly-heatmap-hour product-daily-average-label">Prom. día</strong>
                {dailyAverages.map((average) => (
                  <div
                    className="product-daily-average"
                    key={`daily-${average.day}`}
                    title={`${average.day} · ${units(average.units)} unidades diarias promedio · ${money(average.revenue)} ingreso diario promedio`}
                  >
                    <strong>{units(average.units)}</strong>
                    <span>u.</span>
                  </div>
                ))}
              </div>
              <aside className="hourly-heatmap-scale" aria-label={`Escala de unidades promedio, desde cero hasta ${units(maxUnits)}`}>
                <strong>{units(maxUnits)} u.</strong>
                <span className="hourly-heatmap-gradient product-hourly-gradient" />
                <strong>0 u.</strong>
              </aside>
            </div>
          </div>
        ) : <div className="empty-state">No hay productos vendidos para esta ubicación.</div>
      )}
    </section>
  );
}

export default ProductHourlyWeekdayHeatmap;
